import { Box, CircularProgress, InputAdornment, Stack, Typography } from "@mui/material";
import {
  ActionButton,
  FieldInput,
  sanitizeDecimalMaxLen,
  useNotificationSnackbar,
  validateOptionalDecimalMaxLen,
} from "design-components";
import { useEffect, useState } from "react";
import { SettingsPageShell } from "../components/settings-page-shell.component";
import { useAppDispatch, useAppSelector } from "../../../shared/store/hook";
import {
  fetchBookingSettings,
  saveBookingSettings,
  selectBookingSettingsData,
  selectBookingSettingsError,
  selectBookingSettingsLoading,
  selectBookingSettingsSaveError,
  selectBookingSettingsSaving,
} from "../../../shared/store/bookingSettings";

const DAY_RATE_DECIMAL_PLACES = 2;

const CARD_DESCRIPTION =
  "Set the default day rates for wheeled and stack storage. You can change this for any location and for any booking.";

export const SettingsBookingsPage = () => {
  const dispatch = useAppDispatch();
  const { showNotification, snackbar } = useNotificationSnackbar();

  const data = useAppSelector(selectBookingSettingsData);
  const loading = useAppSelector(selectBookingSettingsLoading);
  const loadError = useAppSelector(selectBookingSettingsError);
  const saving = useAppSelector(selectBookingSettingsSaving);
  const saveError = useAppSelector(selectBookingSettingsSaveError);

  const [stackDayRate, setStackDayRate] = useState("");
  const [wheeledDayRate, setWheeledDayRate] = useState("");
  const [errors, setErrors] = useState<{ stack?: string; wheeled?: string }>({});

  const fieldsDisabled = loading || saving;

  useEffect(() => {
    void dispatch(fetchBookingSettings());
  }, [dispatch]);

  useEffect(() => {
    if (!data) {
      if (loadError) {
        setStackDayRate("");
        setWheeledDayRate("");
      }
      return;
    }
    setStackDayRate(formatDayRateForInput(data.stackDayRate));
    setWheeledDayRate(formatDayRateForInput(data.wheeledDayRate));
  }, [data, loadError]);

  useEffect(() => {
    if (loadError) {
      showNotification({
        variant: "error",
        title: "Could not load booking settings",
        description: loadError,
      });
    }
  }, [loadError, showNotification]);

  useEffect(() => {
    if (saveError) {
      showNotification({
        variant: "error",
        title: "Could not save booking settings",
        description: saveError,
      });
    }
  }, [saveError, showNotification]);

  const handleStackChange = (value: string) => {
    const next = sanitizeDecimalMaxLen(value, 10, DAY_RATE_DECIMAL_PLACES);
    setStackDayRate(next);
    setErrors((prev) => ({ ...prev, stack: undefined }));
  };

  const handleWheeledChange = (value: string) => {
    const next = sanitizeDecimalMaxLen(value, 10, DAY_RATE_DECIMAL_PLACES);
    setWheeledDayRate(next);
    setErrors((prev) => ({ ...prev, wheeled: undefined }));
  };

  const handleCancel = () => {
    if (data) {
      setStackDayRate(formatDayRateForInput(data.stackDayRate));
      setWheeledDayRate(formatDayRateForInput(data.wheeledDayRate));
    } else {
      setStackDayRate("");
      setWheeledDayRate("");
    }
    setErrors({});
  };

  const handleSave = () => {
    const stackErr = validateOptionalDayRate(stackDayRate);
    const wheeledErr = validateOptionalDayRate(wheeledDayRate);
    setErrors({
      stack: stackErr,
      wheeled: wheeledErr,
    });
    if (stackErr || wheeledErr) return;

    const body = {
      stackDayRate: parseDayRate(stackDayRate),
      wheeledDayRate: parseDayRate(wheeledDayRate),
    };

    if (
      data &&
      data.stackDayRate === body.stackDayRate &&
      data.wheeledDayRate === body.wheeledDayRate
    ) {
      return;
    }

    void dispatch(saveBookingSettings({ body })).then((action) => {
      if (!saveBookingSettings.fulfilled.match(action)) return;
      showNotification({
        variant: "success",
        title: "Booking settings saved",
        description: action.payload.message || "Operator booking settings saved successfully.",
      });
    });
  };

  return (
    <SettingsPageShell activeTab="bookings">
      <div className="settings-bookings-page">
        {loading && !data ? (
          <Box className="settings-bookings-page__loading" aria-busy="true">
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Box className="settings-bookings-page__card">
            <Stack spacing={2.5} className="settings-bookings-page__card-body">
              <Stack spacing={1.25}>
                <Typography className="settings-bookings-page__card-title" component="h2">
                  Default day rates
                </Typography>
                <Typography className="settings-bookings-page__card-description">
                  {CARD_DESCRIPTION}
                </Typography>
              </Stack>
              <FieldInput
                className="settings-bookings-page__field"
                label="Stack day rate"
                fullWidth
                disabled={fieldsDisabled}
                error={Boolean(errors.stack)}
                helperText={errors.stack}
                outlinedInputProps={{
                  value: stackDayRate,
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputMode: "decimal",
                  onChange: (e) => handleStackChange(e.target.value),
                }}
              />
              <FieldInput
                className="settings-bookings-page__field"
                label="Wheeled day rate"
                fullWidth
                disabled={fieldsDisabled}
                error={Boolean(errors.wheeled)}
                helperText={errors.wheeled}
                outlinedInputProps={{
                  value: wheeledDayRate,
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputMode: "decimal",
                  onChange: (e) => handleWheeledChange(e.target.value),
                }}
              />
              <Stack direction="row" spacing={1.25} className="settings-bookings-page__actions">
                <ActionButton
                  type="button"
                  actionVariant="contained-primary"
                  size="medium"
                  onClick={handleSave}
                  disabled={fieldsDisabled}
                >
                  Save
                </ActionButton>
                <ActionButton
                  type="button"
                  actionVariant="neutral"
                  size="medium"
                  onClick={handleCancel}
                  disabled={fieldsDisabled}
                >
                  Cancel
                </ActionButton>
              </Stack>
            </Stack>
          </Box>
        )}
      </div>
      {snackbar}
    </SettingsPageShell>
  );
};

function formatDayRateForInput(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "";
  return String(value);
}

function validateOptionalDayRate(raw: string): string | undefined {
  const trimmed = raw.trim().replace(/^\$/, "");
  if (!trimmed) return undefined;
  const formatErr = validateOptionalDecimalMaxLen(trimmed, 10, DAY_RATE_DECIMAL_PLACES);
  if (formatErr) return formatErr;
  const n = Number.parseFloat(trimmed);
  if (!Number.isFinite(n) || n < 0) return "Must be zero or greater";
  return undefined;
}

/** Empty optional field maps to null for the PUT body (FR-DRN-003). */
function parseDayRate(raw: string): number | null {
  const trimmed = raw.trim().replace(/^\$/, "");
  if (!trimmed) return null;
  return Number.parseFloat(trimmed);
}